import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { CheckCircle2, CreditCard } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  getListCardsPaymentAccountCardAllGetQueryKey,
  useActivatePhysicalCardPaymentMethodsPaymentMethodTypeProviderActivatePost,
} from "@/api-v3";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  canActivate,
  type PaymentAccountCard,
} from "@/features/payments/types/payment-account-cards";
import { useToast } from "@/hooks/use-toast";

interface PhysicalCardActivationFormDialogProps {
  disabled: boolean;
  card: PaymentAccountCard;
}

const activationSchema = z.object({
  last4: z
    .string()
    .length(4)
    .regex(/^\d{4}$/),
});

type ActivationFormData = z.infer<typeof activationSchema>;

export const PhysicalCardActivationFormDialog = (
  props: PhysicalCardActivationFormDialogProps
) => {
  const { card, disabled } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { mutateAsync } =
    useActivatePhysicalCardPaymentMethodsPaymentMethodTypeProviderActivatePost();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const form = useForm<ActivationFormData>({
    resolver: zodResolver(activationSchema),
    defaultValues: {
      last4: "",
    },
  });

  const handleActivate = async (last4: string) => {
    if (!canActivate(card.card_type)) {
      throw new Error(`Card type ${card.card_type} cannot be activated`);
    }
    await mutateAsync({
      paymentMethodType: card.card_type,
      provider: card.provider,
      data: { last4 },
    });

    await queryClient.invalidateQueries({
      queryKey: getListCardsPaymentAccountCardAllGetQueryKey(),
    });

    toast({
      description: t("physicalCard_activation_success"),
    });

    setOpen(false);
  };

  const handleSubmit = async (data: ActivationFormData) => {
    try {
      await handleActivate(data.last4);
      setIsSuccess(true);
    } catch (error) {
      console.error(error);
      form.setError("last4", {
        message: t("physicalCard_activation_error"),
      });
    }
  };

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center text-center">
        <div className="rounded-full bg-green-100 p-3 mb-4">
          <CheckCircle2 className="size-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold mb-2">
          {t("physicalCard_activation_success_title")}
        </h3>
        <p className="text-muted-foreground">
          {t("physicalCard_activation_success_description")}
        </p>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-full mt-2" disabled={disabled}>
          <CreditCard className="mr-2 size-4" />
          {t("physicalCard_activate_button")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-6"
          >
            <DialogHeader>
              <DialogTitle> {t("physicalCard_activation_title")}</DialogTitle>
              <DialogDescription>
                {t("physicalCard_activation_label")}
              </DialogDescription>
            </DialogHeader>

            <FormField
              control={form.control}
              name="last4"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <InputOTP
                      {...field}
                      maxLength={4}
                      pattern={REGEXP_ONLY_DIGITS}
                      autoComplete="off"
                      containerClassName="justify-center"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>
                  <FormDescription>
                    {t("physicalCard_activation_hint")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">{t("cancel")}</Button>
              </DialogClose>

              <Button type="submit" loading={form.formState.isSubmitting}>
                {t("physicalCard_activation_submit")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
